import React, { useState } from 'react';
import { useThread } from '../context/ThreadContext';
import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import ImageGenerationDialog from './ai-tools/ImageGenerationDialog';
import SpeechToTextDialog from './ai-tools/SpeechToTextDialog';
import TextToSpeechDialog from './ai-tools/TextToSpeechDialog';

const IntroSection = () => {
  const { dispatch } = useThread();
  const { currentUser } = useContext(AuthContext);
  const [currentTool, setCurrentTool] = useState(null);

  const aiTools = [
    {
      name: 'Image Generation',
      description: 'Create unique images from text descriptions',
      component: ImageGenerationDialog,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    },
    {
      name: 'Speech to Text',
      description: 'Convert audio recordings into text',
      component: SpeechToTextDialog,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14Z" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M17 11C17 14.53 14.39 17.44 11 17.93V21H13V23H11H9V21H11V17.93C7.61 17.44 5 14.53 5 11H7C7 13.76 9.24 16 12 16C14.76 16 17 13.76 17 11H19H17Z" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    },
    {
      name: 'Text to Speech',
      description: 'Convert text into natural-sounding speech',
      component: TextToSpeechDialog,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 9V15H7L12 20V4L7 9H3Z" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M16.5 12C16.5 10.23 15.48 8.71 14 7.97V16.02C15.48 15.29 16.5 13.77 16.5 12Z" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    }
  ];

  const assistants = [
    {
      name: 'HR Assistant',
      description: 'Your dedicated helper for HR policies, employee relations, and workplace matters',
      userPrompt: 'Hello! I am your HR assistant. How can I help you with HR-related matters today?',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12Z" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M20 21C20 16.5 16.42 13 12 13C7.58 13 4 16.5 4 21" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    },
    {
      name: 'Legal Advisor',
      description: 'Get guidance on legal matters and basic legal document assistance',
      userPrompt: 'I can help you understand legal concepts and draft basic legal documents. What do you need help with?',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 3L3 9V21H21V9L12 3Z" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M12 8V16M8 12H16" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    },
    {
      name: 'Technical Support',
      description: 'Troubleshoot technical issues and get IT support assistance',
      userPrompt: 'Hi! I can help you resolve technical issues. What problem are you experiencing?',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 3.5V6.5C14 7.05 14.45 7.5 15 7.5H18" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H14L19 8V19C19 20.1046 18.1046 21 17 21Z" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    },
    {
      name: 'Financial Advisor',
      description: 'Get guidance on personal finance, investments, and budgeting',
      userPrompt: 'I can help you with financial planning and investment advice. What would you like to know?',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M2 17L12 22L22 17" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M2 12L12 17L22 12" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    },
    {
      name: 'Career Coach',
      description: 'Career development advice and job search strategies',
      userPrompt: 'Ready to help you with career planning and professional development. What\'s your career goal?',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V9C22 7.89543 21.1046 7 20 7Z" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M16 7V4C16 2.89543 15.1046 2 14 2H10C8.89543 2 8 2.89543 8 4V7" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    },
    {
      name: 'Marketing Strategist',
      description: 'Help with marketing strategies and campaign planning',
      userPrompt: 'Let\'s develop effective marketing strategies for your business. What\'s your marketing goal?',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 8L2 22M17.5 15H9M13.5 21H9M21 11.5V3.5C21 2.67157 20.3284 2 19.5 2H11.5C10.6716 2 10 2.67157 10 3.5V11.5C10 12.3284 10.6716 13 11.5 13H19.5C20.3284 13 21 12.3284 21 11.5Z" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    }
  ];

  const copilots = [
    {
      name: 'E-mail Writer',
      description: 'Craft professional and effective emails with ease',
      systemPrompt: 'You are an email writing assistant. Your role is to help users craft professional, clear, and effective emails.',
      userPrompt: 'Hello! I can help you write professional emails.',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="4" width="20" height="16" rx="3" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M2 7L12 14L22 7" stroke="#ECECF1" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      )
    },
    {
      name: 'Content Writer',
      description: 'Generate engaging blog posts and articles',
      systemPrompt: 'You are a content writing assistant specializing in creating engaging blog posts and articles.',
      userPrompt: 'I can help you create compelling content. What topic would you like to write about?',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M14 2V8H20" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    },
    {
      name: 'Social Media Manager',
      description: 'Create engaging social media posts and content strategies',
      systemPrompt: 'You are a social media content specialist helping users create engaging posts and content strategies.',
      userPrompt: 'Let\'s create engaging social media content. Which platform are we focusing on?',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 2H6C3.79086 2 2 3.79086 2 6V18C2 20.2091 3.79086 22 6 22H18C20.2091 22 22 20.2091 22 18V6C22 3.79086 20.2091 2 18 2Z" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M8 12H16M12 8V16" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    },
    {
      name: 'Code Assistant',
      description: 'Help with coding problems and code review',
      systemPrompt: 'You are a coding assistant helping users with programming problems and code review.',
      userPrompt: 'I can help you with coding problems. What programming language are you working with?',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 18L22 12L16 6" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M8 6L2 12L8 18" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    },
    {
      name: 'Research Assistant',
      description: 'Help with research, data analysis, and fact-finding',
      systemPrompt: 'You are a research assistant helping users find and analyze information effectively.',
      userPrompt: 'I can help you with research and analysis. What topic are you investigating?',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M21 21L16.65 16.65" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    },
    {
      name: 'Translation Helper',
      description: 'Assist with language translation and cultural context',
      systemPrompt: 'You are a translation assistant helping users translate content while maintaining cultural context.',
      userPrompt: 'I can help you with translations. What languages are we working with?',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.87 15.07L10.33 12.56L10.36 12.53C12.1 10.59 13.34 8.36 14.07 6H17V4H10V2H8V4H1V6H12.17C11.5 7.92 10.44 9.75 9 11.35C8.07 10.32 7.3 9.19 6.69 8H4.69C5.42 9.63 6.42 11.17 7.67 12.56L2.58 17.58L4 19L9 14L12.11 17.11L12.87 15.07ZM18.5 10H16.5L12 22H14L15.12 19H19.87L21 22H23L18.5 10ZM15.88 17L17.5 12.67L19.12 17H15.88Z" stroke="#ECECF1" strokeWidth="1.5"/>
        </svg>
      )
    }
  ];

  const quickActions = [
    {
      name: 'Brainstorm Ideas',
      prompt: 'Help me brainstorm ideas about: ',
      className: 'brainstorm',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.5 19C9.5 19.5523 9.94772 20 10.5 20H13.5C14.0523 20 14.5 19.5523 14.5 19V18H9.5V19Z" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M12 3C8.13401 3 5 6.13401 5 10C5 12.7614 6.67004 15.1175 9.12279 16.1675C9.35912 16.2777 9.5 16.5035 9.5 16.7578V17H14.5V16.7578C14.5 16.5035 14.6409 16.2777 14.8772 16.1675C17.33 15.1175 19 12.7614 19 10C19 6.13401 15.866 3 12 3Z" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    },
    {
      name: 'Analyze Data',
      prompt: 'Can you analyze this data: ',
      className: 'analyze',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 7H20" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M4 12H20" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M4 17H14" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    },
    {
      name: 'Make a Plan',
      prompt: 'Help me create a plan for: ',
      className: 'plan',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="#ECECF1" strokeWidth="2"/>
          <rect x="9" y="3" width="6" height="4" rx="2" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    },
    {
      name: 'Code',
      prompt: 'Write code for: ',
      className: 'code',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 18L22 12L16 6" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M8 6L2 12L8 18" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    },
    {
      name: 'Summarize Text',
      prompt: 'Summarize the following: ',
      className: 'summarize',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 7H20" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M4 12H20" stroke="#ECECF1" strokeWidth="2"/>
          <path d="M4 17H14" stroke="#ECECF1" strokeWidth="2"/>
        </svg>
      )
    }
  ];

  const handleToolSelect = (tool) => {
    setCurrentTool(tool);
  };

  const handleQuickAction = (prompt) => {
    // Get the input textarea element
    const textarea = document.querySelector('.inputPrompttTextarea');
    if (textarea) {
      // Set the value and focus the textarea
      textarea.value = prompt;
      textarea.focus();
      
      // Trigger input event to ensure any listeners are notified
      const event = new Event('input', { bubbles: true });
      textarea.dispatchEvent(event);
    }
  };

  return (
    <div className="intro-section">
      <h1 className="welcome-title">Welcome! How can I help you today?</h1>
      
      {/* <div className="scroll-sections">
        <div className="section-container">
          <h2 className="section-title">AI Agents</h2>
          <div className="scroll-container">
            {aiTools.map((tool, index) => (
              <div 
                key={index} 
                className="item-card"
                onClick={() => handleToolSelect(tool)}
                style={{ cursor: 'pointer' }}
              >
                <div className="item-icon">{tool.icon}</div>
                <div className="item-name">{tool.name}</div>
                <div className="item-description">{tool.description}</div>
              </div>
            ))}
          </div>
        </div> */}

        {/* <div className="section-container">
          <h2 className="section-title">Assistants</h2>
          <div className="scroll-container">
            {assistants.map((assistant, index) => (
              <div key={index} className="item-card">
                <div className="item-icon">{assistant.icon}</div>
                <div className="item-name">{assistant.name}</div>
                <div className="item-description">{assistant.description}</div>
              </div>
            ))}
          </div>
        </div> */}
{/* 
        <div className="section-container">
          <h2 className="section-title">Co-pilots</h2>
          <div className="scroll-container">
            {copilots.map((copilot, index) => (
              <div key={index} className="item-card">
                <div className="item-icon">{copilot.icon}</div>
                <div className="item-name">{copilot.name}</div>
                <div className="item-description">{copilot.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div> */}

      <div className="quick-actions-section">
        <h3 className="quick-actions-title">Or you can ask me to</h3>
        <div className="quick-actions-container">
          {quickActions.map((action, index) => (
            <button
              key={index}
              className="quick-action-button"
              onClick={() => handleQuickAction(action.prompt)}
            >
              <span className={`quick-action-icon ${action.className}`}>{action.icon}</span>
              <span className="quick-action-name">{action.name}</span>
            </button>
          ))}
        </div>
      </div>

      {currentTool && (
        <currentTool.component 
          isOpen={true} 
          onClose={() => setCurrentTool(null)} 
        />
      )}
    </div>
  );
};

export default IntroSection;
